import { ButtonDS, CheckboxAtom, GlobalIcon, Typography } from "get-life-storybook-ts";
import StripeIcon from "get-life-storybook-ts/lib/components/Icons/StripeIcon";
import { useContext, useEffect } from "react";
import CardSetupForm from "../../../PaymentGateway/components/CardSetupForm/CardSetupForm.component";
import { StripeContext } from "../../../../../../context/StripeContext.context";
import { LanguageT } from "../../../../../../context/GetLifeContext.context";
import { ConditionsLink, PrivacityLink } from "../../../../../../utils/InternationlChanges";
import { DOMAIN } from "../../../../../../constants/Global";
import numberFormat from "../../../../../../utils/Utils";
import FormLabel from "./FormLabel.component";
import PaymentMethodPicker, { WalletType } from "./PaymentMethodPicker.component";
import WalletButton from "./WalletButton.component";
import { NewCheckoutI } from "../../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { useWalletAvailability } from "../hooks/useWalletAvailability";

interface CardFormSectionProps {
  enabled: boolean;
  setupIntent: any;
  checkout: NewCheckoutI;
  handleStripeStatus: (...args: any[]) => void;
  stripeInputStatus: any;
  payConfirm: boolean;
  payConfirm2: boolean;
  handleCheckbox: () => void;
  handleCheckbox2: () => void;
  isButtonDisabled: boolean;
  selectedMethod: WalletType;
  onMethodChange: (m: WalletType) => void;
  translate: (key: string) => string;
}

const buildSentence = (
  message: string,
  symbol: "#" | "$",
  replacement: React.ReactNode
): React.ReactNode => {
  const idx = message.indexOf(symbol);
  if (idx < 0) return message;
  return (
    <>
      {message.substring(0, idx)}
      {replacement}
      {message.substring(idx + 1)}
    </>
  );
};

const LegalLabel = ({ translate }: { translate: (k: string) => string }) => {
  const locale = process.env.REACT_APP_DOMAIN_LOCALE as LanguageT;
  const conditions = ConditionsLink[locale];
  const privacity = PrivacityLink[locale];

  return (
    <Typography variant="BodyMedium" text="" color="dark-gray-100">
      {buildSentence(
        translate("payment.paymentCard.checkBox.unifiedMessage"),
        "$",
        <Typography
          variant="SpanMedium"
          weight="700"
          text={numberFormat(0.5, "price")}
        />
      )}
      {locale === "fr" &&
        buildSentence(
          translate("payment.paymentCard.checkBox.cotisation"),
          "$",
          <Typography
            variant="SpanMedium"
            weight="700"
            text={numberFormat(2.5, "price")}
          />
        )}
      <br />
      {buildSentence(
        translate("payment.paymentCard.checkBox.conditionsMessage"),
        "$",
        <span data-track="conditions">
          <Typography
            variant="LinkMedium"
            text={translate("payment.paymentCard.checkBox.conditionLink")}
            href={conditions}
            target="_blank"
            rel="noreferrer"
          />
        </span>
      )}
      {buildSentence(
        translate("payment.paymentCard.checkBox.policyMessage"),
        "$",
        <Typography
          variant="LinkMedium"
          text={translate("payment.paymentCard.checkBox.policyLink")}
          href={privacity}
          target="_blank"
          rel="noreferrer"
        />
      )}
    </Typography>
  );
};

const CardFormSection = ({
  enabled,
  selectedMethod,
  onMethodChange,
  setupIntent,
  checkout,
  handleStripeStatus,
  stripeInputStatus,
  payConfirm,
  payConfirm2,
  handleCheckbox,
  handleCheckbox2,
  isButtonDisabled,
  translate,
}: CardFormSectionProps) => {
  const { handleSubmit } = useContext(StripeContext);
  const {
    isGooglePayAvailable,
    isApplePayAvailable,
    paymentRequest,
  } = useWalletAvailability(translate("paymentV3.wallet.totalLabel"));
  const isCard = selectedMethod === "card";
  const isGooglePay = selectedMethod === "google_pay";
  const isApplePay = selectedMethod === "apple_pay";
  const isWallet = isGooglePay || isApplePay;
  const legalAccepted = payConfirm && (DOMAIN !== "it" || payConfirm2);

  // If the selected wallet becomes unavailable (e.g. after a re-detect),
  // bring the user back to the card flow so they're never stuck on a hidden option.
  useEffect(() => {
    if (selectedMethod === "google_pay" && !isGooglePayAvailable) {
      onMethodChange("card");
    } else if (selectedMethod === "apple_pay" && !isApplePayAvailable) {
      onMethodChange("card");
    }
  }, [selectedMethod, isGooglePayAvailable, isApplePayAvailable, onMethodChange]);

  return (
    <>
      <section
        className={`paymentV3Section ${enabled ? "" : "paymentV3Section--locked"}`}
      >
        <FormLabel
          num="02"
          title={translate("paymentV3.section.method.title")}
          active={enabled}
        />
        <div className="paymentV3Section__body">
          <PaymentMethodPicker
            selected={selectedMethod}
            onChange={onMethodChange}
            translate={translate}
            googlePayAvailable={isGooglePayAvailable}
            applePayAvailable={isApplePayAvailable}
          />
        </div>
      </section>

      {isCard && (
        <section
          className={`paymentV3Section ${enabled ? "" : "paymentV3Section--locked"}`}
        >
          <FormLabel
            num="03"
            title={translate("paymentV3.section.cardData.title")}
            active={enabled}
          />
          <div className="paymentV3Section__body paymentV3Section__cardForm">
            {Object.keys(setupIntent || {}).length !== 0 && (
              <CardSetupForm
                translate={translate}
                stripeInputStatus={stripeInputStatus}
                handleStripeStatus={handleStripeStatus}
              />
            )}
          </div>
        </section>
      )}

      <div
        className={`paymentV3LegalBlock ${enabled ? "" : "paymentV3LegalBlock--locked"}`}
      >
        <CheckboxAtom
          isLegal
          checked={payConfirm}
          onValueChange={handleCheckbox}
          label={<LegalLabel translate={translate} />}
        />
        {DOMAIN === "it" && (
          <CheckboxAtom
            isLegal
            checked={payConfirm2}
            onValueChange={handleCheckbox2}
            label={
              <Typography
                variant="BodyMedium"
                text={translate("paymentCard.second.checkbox")}
                color="dark-gray-100"
              />
            }
          />
        )}
      </div>

      {isCard && (
        <div
          className={`paymentV3CTABlock paymentV3CTABlock--inline paymentV3CTABlock--card ${enabled ? "" : "paymentV3CTABlock--locked"}`}
        >
          <div className="paymentV3CTABlock__provider">
            <Typography
              weight="700"
              color="purple"
              variant="BodyXSmall"
              text={translate("paymentCard.paymentStripe")}
            />
            <StripeIcon />
          </div>
          <ButtonDS
            label={translate("paymentV3.section.cta.verify")}
            leftIcon="LockIcon"
            rightIcon="ArrowRightIcon"
            onClick={() => handleSubmit()}
            disabled={isButtonDisabled}
            className="w-full"
          />
          <div className="paymentV3CTABlock__disclaimer">
            <GlobalIcon iconName="InfoIcon" size="XS" color="#8E8E8E" />
            <span>{translate("paymentV3.section.cta.disclaimer")}</span>
          </div>
        </div>
      )}

      {isWallet && paymentRequest && (
        <div
          className={`paymentV3CTABlock paymentV3CTABlock--inline paymentV3CTABlock--wallet ${enabled ? "" : "paymentV3CTABlock--locked"}`}
        >
          <WalletButton
            paymentRequest={paymentRequest}
            walletType={isApplePay ? "apple_pay" : "google_pay"}
            legalAccepted={legalAccepted}
            translate={translate}
          />
          <div className="paymentV3CTABlock__disclaimer">
            <GlobalIcon iconName="InfoIcon" size="XS" color="#8E8E8E" />
            <span>{translate("paymentV3.section.cta.disclaimer")}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default CardFormSection;
